import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  IoSwapHorizontalOutline,
  IoArrowDownOutline,
  IoArrowUpOutline,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoCalendarOutline,
  IoStorefrontOutline,
  IoCubeOutline,
  IoCreateOutline,
  IoCheckmarkDoneOutline,
  IoTimeOutline,
} from "react-icons/io5";
import api from "@/api";
import { useI18n, STORE_LABELS } from "@/i18n";
import { useAuth } from "@/auth";
import { AppHeader } from "@/components/AppHeader";
import { formatDateIT } from "@/utils";
import { tapSuccess, tapError } from "@/utils/haptics";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

export default function ReservationsPage() {
  const { t } = useI18n();
  const { user, isTitolare } = useAuth();
  const [tab, setTab] = useState("incoming"); // incoming | outgoing | history
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRes, setEditRes] = useState(null);
  const [editQty, setEditQty] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const ACTIVE_STATUSES = ["pending", "confirmed"];
  const HISTORIC_STATUSES = ["collected", "cancelled", "rejected"];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (!isTitolare && tab !== "history") params.role_filter = tab;
      const { data } = await api.get("/reservations", { params });
      // Client-side split: active vs historic
      let filtered = data;
      if (tab === "history") {
        filtered = data.filter((r) => HISTORIC_STATUSES.includes(r.status));
      } else {
        filtered = data.filter((r) => ACTIVE_STATUSES.includes(r.status));
      }
      // Sort: active by newest first, history by most-recently-changed
      filtered.sort((a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || ""));
      setItems(filtered);
    } catch {
      toast.error("Errore caricamento prenotazioni");
    } finally {
      setLoading(false);
    }
  }, [tab, isTitolare]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (path, id, successMsg) => {
    try {
      await api.post(`/reservations/${id}${path}`);
      tapSuccess();
      toast.success(successMsg);
      load();
    } catch (e) {
      tapError();
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const openEdit = (r) => {
    setEditRes(r);
    setEditQty(String(r.quantity));
  };

  const submitEdit = async () => {
    const n = parseInt(editQty, 10);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Quantità non valida");
      return;
    }
    if (n === editRes.quantity) {
      setEditRes(null);
      return;
    }
    setEditSaving(true);
    try {
      await api.put(`/reservations/${editRes.id}`, { quantity: n });
      tapSuccess();
      toast.success(`Quantità aggiornata a ${n}`);
      setEditRes(null);
      load();
    } catch (e) {
      tapError();
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div className="mobile-frame" data-testid="reservations-page">
      <AppHeader title={t("reservations")} showBack backTo="/" />

      <div className="tf-chips" style={{ padding: "8px 20px" }}>
        {!isTitolare && (
          <>
            <button
              data-testid="tab-incoming"
              className={`tf-chip ${tab === "incoming" ? "active" : ""}`}
              onClick={() => setTab("incoming")}
            >
              <IoArrowDownOutline size={14} />
              {t("reservations_incoming")}
            </button>
            <button
              data-testid="tab-outgoing"
              className={`tf-chip ${tab === "outgoing" ? "active" : ""}`}
              onClick={() => setTab("outgoing")}
            >
              <IoArrowUpOutline size={14} />
              {t("reservations_outgoing")}
            </button>
          </>
        )}
        <button
          data-testid="tab-history"
          className={`tf-chip ${tab === "history" ? "active" : ""}`}
          onClick={() => setTab("history")}
        >
          <IoTimeOutline size={14} />
          {t("reservations_history")}
        </button>
      </div>

      <div className="tf-content">
        {loading && <div className="tf-empty">{t("loading")}</div>}
        {!loading && items.length === 0 && (
          <div className="tf-empty" data-testid="reservations-empty">
            <IoSwapHorizontalOutline size={44} style={{ opacity: 0.4 }} />
            <p>{t("no_reservations")}</p>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {items.map((r) => {
            const isSource = user?.storeId === r.source_store_id;
            const isTarget = user?.storeId === r.target_store_id;
            const isPending = r.status === "pending";
            const isConfirmed = r.status === "confirmed";
            const canAccept = isPending && (isSource || isTitolare);
            const canEdit = isPending && (isSource || isTitolare);
            const canCollect = isConfirmed && (isTarget || isTitolare);
            const canCancel = (isPending || isConfirmed) && (isSource || isTarget || isTitolare);
            return (
              <div key={r.id} className="tf-reservation-card" data-testid={`reservation-${r.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 8 }}>
                  <div>
                    <div className="tf-reservation-name">{r.product_name}</div>
                    <div className="tf-small tf-row" style={{ gap: 6, marginTop: 4 }}>
                      <IoStorefrontOutline size={13} />
                      {t(STORE_LABELS[r.source_store_id])}
                      <IoArrowDownOutline size={13} style={{ transform: "rotate(-90deg)" }} />
                      {t(STORE_LABELS[r.target_store_id])}
                    </div>
                  </div>
                  <span className={`tf-reservation-status tf-reservation-status-${r.status}`}>
                    {t(`reservation_status_${r.status}`) || r.status}
                  </span>
                </div>
                <div className="tf-row" style={{ gap: 12, marginTop: 8, flexWrap: "wrap" }}>
                  <span className="tf-small tf-row" style={{ gap: 4 }}>
                    <IoCubeOutline size={13} /> {r.quantity} pz
                  </span>
                  {r.pickup_date && (
                    <span className="tf-small tf-row" style={{ gap: 4 }}>
                      <IoCalendarOutline size={13} /> {formatDateIT(r.pickup_date)}
                    </span>
                  )}
                </div>
                {r.notes && <div className="tf-small" style={{ marginTop: 8 }}>{r.notes}</div>}
                {(canAccept || canEdit || canCollect || canCancel) && (
                  <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                    {canAccept && !isTitolare && (
                      <button
                        data-testid={`reservation-accept-${r.id}`}
                        className="tf-btn-primary"
                        style={{ flex: 1, minWidth: 120 }}
                        onClick={() => doAction("/confirm", r.id, t("reservation_confirmed"))}
                      >
                        <IoCheckmarkDoneOutline size={18} />
                        {t("accept")}
                      </button>
                    )}
                    {canCollect && (
                      <button
                        data-testid={`reservation-collect-${r.id}`}
                        className="tf-btn-primary"
                        style={{ flex: 1, minWidth: 120 }}
                        onClick={() => doAction("/collect", r.id, t("reservation_collected"))}
                      >
                        <IoCheckmarkCircle size={18} />
                        {t("collect")}
                      </button>
                    )}
                    {canEdit && !isTitolare && (
                      <button
                        data-testid={`reservation-edit-qty-${r.id}`}
                        className="tf-btn-outline-red"
                        style={{ flex: 1, minWidth: 120 }}
                        onClick={() => openEdit(r)}
                      >
                        <IoCreateOutline size={18} />
                        {t("edit_quantity")}
                      </button>
                    )}
                    {canCancel && (
                      <button
                        data-testid={`reservation-cancel-${r.id}`}
                        className="tf-btn-ghost"
                        style={{ flex: 1, minWidth: 120 }}
                        onClick={() => doAction("/cancel", r.id, t("reservation_cancelled"))}
                      >
                        <IoCloseCircle size={18} />
                        {isSource && !isTitolare ? t("reject") : t("cancel")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Drawer open={Boolean(editRes)} onOpenChange={(o) => !o && setEditRes(null)}>
        <DrawerContent data-testid="edit-qty-drawer">
          <DrawerHeader>
            <DrawerTitle className="tf-drawer-title">
              <IoCreateOutline size={22} />
              {t("edit_quantity")}
            </DrawerTitle>
          </DrawerHeader>
          <div className="tf-drawer-body">
            {editRes && (
              <p className="tf-muted" style={{ marginTop: 0 }}>
                {editRes.product_name} · attuale: <strong>{editRes.quantity}</strong>
              </p>
            )}
            <div className="tf-field">
              <label className="tf-label">{t("reserved_quantity")} *</label>
              <input
                data-testid="edit-qty-input"
                className="tf-input"
                type="number"
                min={1}
                value={editQty}
                onChange={(e) => setEditQty(e.target.value)}
                inputMode="numeric"
                autoFocus
              />
            </div>
            <button
              data-testid="edit-qty-confirm"
              className="tf-btn-primary"
              disabled={editSaving}
              onClick={submitEdit}
            >
              <IoCheckmarkCircle size={18} />
              {editSaving ? t("saving") : t("confirm")}
            </button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
